import React, { useState, useEffect } from 'react';
import { fetchPacientes, deletePaciente, fetchClinicalRecords } from '../../servicios/apiClinica';
import FormularioPaciente from './FormularioPaciente';
import VisorHistoriaClinica from './VisorHistoriaClinica';
import CertificadoMedico from './CertificadoMedico';
import RegistroFotografico from './RegistroFotografico';
import VisorConsultaModal from './VisorConsultaModal';
import ConfirmModal from '../../comun/ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Pacientes = () => {
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showHistoriaModal, setShowHistoriaModal] = useState(false);
    const [showCertificadoModal, setShowCertificadoModal] = useState(false);
    const [showRegistroFotograficoModal, setShowRegistroFotograficoModal] = useState(false);
    const [showConsultaModal, setShowConsultaModal] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'view', 'edit'
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [clinicalData, setClinicalData] = useState(null);
    const [quoteIdToComplete, setQuoteIdToComplete] = useState(null);
    const [showObsModal, setShowObsModal] = useState(false);
    const [selectedObsPatient, setSelectedObsPatient] = useState(null);
    const [observations, setObservations] = useState([]);
    const [savingObs, setSavingObs] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [filtroVista, setFiltroVista] = useState('clinico'); // 'clinico', 'agenda'
    const [searchTerm, setSearchTerm] = useState('');

    const loadPacientes = async (autoCompleteQuoteId = null, autoCompletePatientId = null) => {
        setLoading(true);
        const data = await fetchPacientes();
        
        try {
            const token = localStorage.getItem('token');
            const recordsResponse = await fetch(`${API_URL}/api/clinical-records/?limit=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            let allRecords = [];
            if (recordsResponse.ok) {
                const rData = await recordsResponse.json();
                allRecords = rData.results || rData || [];
            }
            
            const pacientesConHistorial = data.map(p => {
                const pRecords = allRecords.filter(r => String(r.patient) === String(p.id) || (r.patient && String(r.patient.id) === String(p.id)));
                const hasRealHistory = pRecords.some(r => r.reason_for_consultation !== 'Cotización / Presupuesto' && r.reason_for_consultation !== 'Cotización / Presupuesto (Completado)');
                const quoteRecord = pRecords.find(r => r.reason_for_consultation === 'Cotización / Presupuesto' || r.reason_for_consultation === 'Cotización / Presupuesto (Completado)');
                return { ...p, hasRealHistory, quoteRecord };
            });
            pacientesConHistorial.sort((a, b) => {
                const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                return dateB - dateA;
            });
            setPacientes(pacientesConHistorial);

            // Si se pasa un paciente o cotización por URL para completar automáticamente
            if (autoCompleteQuoteId || autoCompletePatientId) {
                const targetPatient = pacientesConHistorial.find(p => 
                    (autoCompletePatientId && String(p.id) === String(autoCompletePatientId)) ||
                    (autoCompleteQuoteId && p.quoteRecord && String(p.quoteRecord.id) === String(autoCompleteQuoteId))
                );
                if (targetPatient) {
                    handleCompletarHistorial(targetPatient);
                }
            }
        } catch (e) {
            console.error(e);
            setPacientes(data);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const qId = urlParams.get('quote_id');
        const pId = urlParams.get('patient_id');
        loadPacientes(qId, pId);
    }, []);

    const handleSave = () => {
        setShowModal(false);
        loadPacientes();
    };

    const handleOpenObservations = (p) => {
        setSelectedObsPatient(p);
        // p.clinical_observations is a JSON list of objects [{id: 1, text: '', resolved: false}]
        let obsList = p.clinical_observations;
        if (typeof obsList === 'string') {
            try { obsList = JSON.parse(obsList); } catch(e) { obsList = []; }
        }
        setObservations(Array.isArray(obsList) ? [...obsList] : []);
        setShowObsModal(true);
    };

    const handleAddObservation = () => {
        setObservations([...observations, { id: Date.now(), text: '', resolved: false }]);
    };

    const handleUpdateObservation = (index, field, value) => {
        const newObs = [...observations];
        newObs[index][field] = value;
        setObservations(newObs);
    };

    const handleRemoveObservation = (index) => {
        const newObs = [...observations];
        newObs.splice(index, 1);
        setObservations(newObs);
    };

    const handleSaveObservations = async () => {
        if (!selectedObsPatient) return;
        setSavingObs(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${selectedObsPatient.id}/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    clinical_observations: observations
                })
            });
            if (response.ok) {
                setShowObsModal(false);
                loadPacientes(); // reload to show updated colors
            } else {
                alert('Error al guardar observaciones');
            }
        } catch (e) {
            console.error(e);
            alert('Error de conexión');
        }
        setSavingObs(false);
    };

    const handleCreate = () => {
        setSelectedPatient(null);
        setClinicalData(null);
        setQuoteIdToComplete(null);
        setModalMode('create');
        setShowModal(true);
    };

    const handlePromoteToClinical = async (paciente) => {
        if (!window.confirm(`¿Estás seguro de que deseas mover a ${paciente.first_name || 'Paciente'} al historial clínico?`)) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${paciente.id}/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_clinical: true })
            });
            if (response.ok) {
                alert('Paciente movido exitosamente al historial clínico.');
                loadPacientes();
            } else {
                alert('Error al actualizar el estado del paciente.');
            }
        } catch (e) {
            console.error(e);
            alert('Error de conexión.');
        }
    };

    const loadPatientClinicalData = async (patientId) => {
        const records = await fetchClinicalRecords(patientId);
        if (records && records.length > 0) {
            const isQuote = (r) => r.reason_for_consultation === 'Cotización / Presupuesto';
            const isCompletedQuote = (r) => r.reason_for_consultation === 'Cotización / Presupuesto (Completado)';

            const realHistories = records.filter(r => !isQuote(r) && !isCompletedQuote(r));
            const basePool = realHistories.length > 0
                ? realHistories
                : records.filter(isCompletedQuote);

            if (basePool.length === 0 && records.length > 0) {
                setClinicalData(records[0] || null);
                return;
            }

            const score = (r) => {
                const rows = Array.isArray(r.treatments) ? r.treatments : [];
                const conTexto = rows.filter(t =>
                    (t.procedures && String(t.procedures).trim()) ||
                    (t.diagnostics_complications && String(t.diagnostics_complications).trim()) ||
                    (t.prescriptions && String(t.prescriptions).trim())
                ).length;
                return { texto: conTexto, total: rows.length };
            };

            const baseRecord = basePool.reduce((best, r) => {
                const s = score(r);
                const b = score(best);
                if (s.texto > b.texto) return r;
                if (s.texto === b.texto && s.total > b.total) return r;
                if (s.texto === b.texto && s.total === b.total && new Date(r.updated_at || 0) > new Date(best.updated_at || 0)) return r;
                return best;
            }, basePool[0]);

            const hasSession = (t) => t && (!!t.date || !!t.appointment_id);
            const textLength = (t) => `${t.procedures || ''}${t.diagnostics_complications || ''}${t.prescriptions || ''}`.trim().length;

            const seen = new Map();
            records.forEach(r => {
                (r.treatments || []).forEach(t => {
                    if (!hasSession(t)) return;
                    const key = t.appointment_id || `${t.date || ''}|${t.time || ''}|${t.procedures || ''}|${t.diagnostics_complications || ''}`;
                    if (!seen.has(key)) {
                        seen.set(key, t);
                    } else if (t.appointment_id && textLength(t) > textLength(seen.get(key))) {
                        seen.set(key, t);
                    }
                });
            });

            setClinicalData({ ...baseRecord, treatments: Array.from(seen.values()) });
        } else {
            setClinicalData(null);
        }
    };

    const handleView = async (patient) => {
        setSelectedPatient(patient);
        setQuoteIdToComplete(null);
        setModalMode('view');
        await loadPatientClinicalData(patient.id);
        setShowModal(true);
    };

    const handleViewHistoria = async (patient) => {
        setSelectedPatient(patient);
        await loadPatientClinicalData(patient.id);
        setShowHistoriaModal(true);
    };

    const handleCompletarHistorial = (patient) => {
        setSelectedPatient(patient);
        if (patient.quoteRecord) {
            setQuoteIdToComplete(patient.quoteRecord.id);
            // Inicializamos el clinicalData con los datos del presupuesto
            const quoteTreatments = patient.quoteRecord.treatments || [];
            // Convertimos los tratamientos del presupuesto en la primera sesión de la Fase 12
            const serviceIds = quoteTreatments.map(t => {
                // Buscaremos el ID del servicio por nombre (o usaremos un array de nombres si no hay IDs directos en el presupuesto)
                // Como el presupuesto solo guarda 'procedimiento', lo pasamos directo, el FormularioPaciente o Fase12 lo procesarán.
                return t.procedimiento;
            }).filter(Boolean);
            
            const initialFase12 = {
                date: new Date().toISOString().split('T')[0],
                time: new Date().toTimeString().split(' ')[0].substring(0, 5),
                diagnostics_complications: 'Paciente inicia tratamiento basado en presupuesto',
                service_ids: serviceIds,
                quote_prices: quoteTreatments.map(t => {
                    const originalPrice = parseFloat(t.precio || 0) * parseInt(t.cantidad || 1);
                    const finalPrice = parseFloat(t.total_final !== undefined ? t.total_final : originalPrice);
                    return { 
                        name: t.procedimiento, 
                        price: originalPrice,
                        discount: originalPrice > finalPrice ? originalPrice - finalPrice : 0
                    };
                }),
                procedures: quoteTreatments.map(t => `${t.cantidad || 1}x ${t.procedimiento}`).join(', '),
                prescriptions: '',
                cie_code: ''
            };

            const { id, ...quoteDataWithoutId } = patient.quoteRecord;
            setClinicalData({
                ...quoteDataWithoutId,
                reason_for_consultation: 'Tratamiento', // Convertir en un historial real
                treatments: [initialFase12, ...Array.from({ length: 8 }, () => ({}))]
            });
        } else {
            setClinicalData(null);
        }
        setModalMode('edit');
        setShowModal(true);
    };

    const handleViewCertificado = async (patient) => {
        const missing = [];
        if (!patient.first_name) missing.push("Nombre(s)");
        if (!patient.last_name) missing.push("Apellido(s)");
        if (!patient.cedula) missing.push("Cédula");
        if (!patient.birth_date) missing.push("Fecha de Nacimiento");
        if (!patient.address) missing.push("Dirección (Domicilio)");

        if (missing.length > 0) {
            setMissingFields(missing);
            setShowMissingFieldsModal(true);
            return;
        }

        setSelectedPatient(patient);
        await loadPatientClinicalData(patient.id);
        setShowCertificadoModal(true);
    };

    const handleRegistroFotografico = (patient) => {
        setSelectedPatient(patient);
        setShowRegistroFotograficoModal(true);
    };

    const handleViewConsulta = async (patient) => {
        setSelectedPatient(patient);
        if (patient.quoteRecord) {
            const r = patient.quoteRecord;
            const quoteDate = new Date(r.updated_at || r.created_at);
            const savedDiscount = r.diagnostic_plans?.discountPercentage !== undefined 
                ? r.diagnostic_plans.discountPercentage 
                : (r.discountPercentage || 0);

            setSelectedQuote({
                id: r.id,
                db_patient_id: patient.id,
                status: r.reason_for_consultation?.includes('Completado') ? 'Completada' : 'Borrador',
                date: quoteDate.toLocaleDateString(),
                rawDate: quoteDate,
                form: {
                    nombre: `${patient.first_name || ''} ${patient.last_name || ''}`.trim().toUpperCase(),
                    cedula: patient.cedula || '',
                    representante: (patient.representative || '').toUpperCase(),
                    cedulaRepresentante: patient.representative_cedula || '',
                    fechaNacimiento: patient.birth_date || '',
                    direccion: patient.address || '',
                    telefono: patient.phone || '',
                    correo: patient.email || '',
                    ocupacion: patient.occupation || '',
                    discountPercentage: savedDiscount
                },
                odontogramaData: r.odontogram_data || {},
                treatments: r.treatments ? r.treatments.map(t => ({
                    ...t,
                    total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))
                })) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
            });
            setShowConsultaModal(true);
        } else {
            // Fallback: check if there's any clinical record for this patient
            const records = await fetchClinicalRecords(patient.id);
            const quoteR = records?.find(r => r.reason_for_consultation?.includes('Cotización / Presupuesto')) || records?.[0];
            if (quoteR) {
                const quoteDate = new Date(quoteR.updated_at || quoteR.created_at);
                const savedDiscount = quoteR.diagnostic_plans?.discountPercentage !== undefined 
                    ? quoteR.diagnostic_plans.discountPercentage 
                    : 0;

                setSelectedQuote({
                    id: quoteR.id,
                    db_patient_id: patient.id,
                    status: 'Cotización',
                    date: quoteDate.toLocaleDateString(),
                    form: {
                        nombre: `${patient.first_name || ''} ${patient.last_name || ''}`.trim().toUpperCase(),
                        cedula: patient.cedula || '',
                        representante: (patient.representative || '').toUpperCase(),
                        cedulaRepresentante: patient.representative_cedula || '',
                        fechaNacimiento: patient.birth_date || '',
                        direccion: patient.address || '',
                        telefono: patient.phone || '',
                        correo: patient.email || '',
                        ocupacion: patient.occupation || '',
                        discountPercentage: savedDiscount
                    },
                    odontogramaData: quoteR.odontogram_data || {},
                    treatments: quoteR.treatments || [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
                });
                setShowConsultaModal(true);
            } else {
                alert('Este paciente no tiene una cotización o presupuesto registrado.');
            }
        }
    };

    const handleEdit = async (patient) => {
        setSelectedPatient(patient);
        setQuoteIdToComplete(null);
        setModalMode('edit');
        await loadPatientClinicalData(patient.id);
        setShowModal(true);
    };

    const handleDelete = async (patient) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar al paciente ${patient.first_name} ${patient.last_name}? Esta acción también eliminará todo su historial clínico y citas relacionadas.`)) {
            try {
                await deletePaciente(patient.id);
                loadPacientes();
            } catch (error) {
                alert('Error al eliminar el paciente. Por favor, inténtelo de nuevo.');
            }
        }
    };

    const confirmCloseModal = (closeFn) => {
        setConfirmAction(() => closeFn);
    };

    const filteredPacientes = pacientes.filter(p => {
        const matchTab = filtroVista === 'clinico' ? p.is_clinical !== false : p.is_clinical === false;
        if (!matchTab) return false;
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
        return fullName.includes(term) || (p.cedula && p.cedula.toLowerCase().includes(term));
    });

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 style={{ color: '#b87a00', fontWeight: 'bold' }}>Pacientes</h2>
                <button 
                    onClick={handleCreate}
                    className="btn shadow-sm" 
                    style={{ backgroundColor: '#28a745', color: 'white', fontWeight: 'bold', borderRadius: '8px' }}
                >
                    <i className="bi bi-person-plus-fill me-2"></i> Nuevo Paciente
                </button>
            </div>

            <div className="d-flex mb-3 gap-2 align-items-center flex-wrap">
                <button 
                    className={`btn ${filtroVista === 'clinico' ? 'btn-primary' : 'btn-outline-primary'}`} 
                    style={{ borderRadius: '20px', fontWeight: 'bold' }}
                    onClick={() => setFiltroVista('clinico')}
                >
                    Pacientes Clínicos
                </button>
                <button 
                    className={`btn ${filtroVista === 'agenda' ? 'btn-primary' : 'btn-outline-primary'}`} 
                    style={{ borderRadius: '20px', fontWeight: 'bold' }}
                    onClick={() => setFiltroVista('agenda')}
                >
                    Solo Agenda
                </button>
                <div className="ms-auto position-relative" style={{ minWidth: '250px' }}>
                    <i className="bi bi-search position-absolute text-muted" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}></i>
                    <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Buscar por nombre o cédula..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ borderRadius: '20px', paddingLeft: '35px' }}
                    />
                </div>
            </div>

            <div className="card shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                <div className="card-body p-0">
                    <table className="table table-hover mb-0">
                        <thead style={{ backgroundColor: '#b87a00', color: 'white' }}>
                            <tr>
                                <th className="py-3 px-4 border-0">Nombres</th>
                                <th className="py-3 px-4 border-0">Apellidos</th>
                                <th className="py-3 px-4 border-0">Cédula / HCL</th>
                                <th className="py-3 px-4 border-0">Teléfono</th>
                                <th className="py-3 px-4 border-0">Grupo Edad</th>
                                <th className="py-3 px-4 border-0 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4">Cargando pacientes...</td>
                                </tr>
                            ) : filteredPacientes.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">No se encontraron pacientes.</td>
                                </tr>
                            ) : (
                                filteredPacientes.map((p) => (
                                    <tr key={p.id}>
                                        <td className="py-3 px-4">{p.first_name || 'Paciente'}</td>
                                        <td className="py-3 px-4">{p.last_name || 'Anónimo'}</td>
                                        <td className="py-3 px-4">{p.cedula || <span className="text-muted">N/A</span>}</td>
                                        <td className="py-3 px-4">{p.phone || <span className="text-muted">N/A</span>}</td>
                                        <td className="py-3 px-4">
                                            <span className="badge" style={{ backgroundColor: '#e2f0d9', color: '#385623' }}>
                                                {(p.age !== undefined && p.age !== null) ? `${p.age} años` : 'No especificado'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            <div className="btn-group bg-light rounded shadow-sm d-inline-flex align-items-center justify-content-center" style={{ padding: '2px' }}>
                                                {filtroVista === 'agenda' && (
                                                    <button onClick={() => handlePromoteToClinical(p)} className="btn btn-sm text-white border-0 rounded me-1" style={{ backgroundColor: '#10b981' }} title="Mover a Historial Clínico">
                                                        <i className="bi bi-person-check-fill"></i>
                                                    </button>
                                                )}
                                                {filtroVista === 'clinico' && p.hasRealHistory === false && (
                                                    <button onClick={() => handleCompletarHistorial(p)} className="btn btn-sm text-white border-0 rounded me-1" style={{ backgroundColor: '#fd7e14' }} title="Completar Historial">
                                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                                    </button>
                                                )}
                                                <button onClick={() => handleView(p)} className="btn btn-sm btn-light border-0" title="Ver Detalle">
                                                    <i className="bi bi-eye-fill text-dark"></i>
                                                </button>
                                                <button onClick={() => handleEdit(p)} className="btn btn-sm btn-light border-0" title="Editar">
                                                    <i className="bi bi-pencil-fill text-dark"></i>
                                                </button>
                                                <button onClick={() => handleDelete(p)} className="btn btn-sm btn-light border-0" title="Eliminar">
                                                    <i className="bi bi-trash-fill text-dark"></i>
                                                </button>
                                                
                                                {p.hasRealHistory !== false && (
                                                    <button onClick={() => handleViewCertificado(p)} className="btn btn-sm btn-light border-0" title="Emitir Certificado">
                                                        <i className="bi bi-patch-check-fill text-dark"></i>
                                                    </button>
                                                )}
                                                <button onClick={() => handleViewConsulta(p)} className="btn btn-sm btn-light border-0" title="Ver Presupuesto / Consulta">
                                                    <i className="bi bi-receipt text-primary"></i>
                                                </button>
                                                <button onClick={() => handleRegistroFotografico(p)} className="btn btn-sm btn-light border-0" title="Registro Fotográfico y Radiológico">
                                                    <i className="bi bi-camera-fill text-dark"></i>
                                                </button>

                                                <button 
                                                    onClick={() => handleOpenObservations(p)} 
                                                    className="btn btn-sm text-white border-0 rounded" 
                                                    style={{
                                                        backgroundColor: (() => {
                                                            let obs = p.clinical_observations;
                                                            if (typeof obs === 'string') {
                                                                try { obs = JSON.parse(obs); } catch(e) { obs = []; }
                                                            }
                                                            if (Array.isArray(obs) && obs.some(o => !o.resolved)) {
                                                                return '#dc3545'; // Red if any pending
                                                            }
                                                            return '#28a745'; // Green if all resolved or none
                                                        })()
                                                    }}
                                                    title="Observaciones"
                                                >
                                                    <i className="bi bi-lightbulb-fill"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '2vh', paddingBottom: '2vh' }}>
                    <div style={{ width: '95%', maxWidth: '1200px', maxHeight: '96vh', backgroundColor: '#fff', borderRadius: '10px', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#0d6efd', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                                {modalMode === 'create' ? 'Registro Médico / Nuevo Paciente' : 
                                 modalMode === 'edit' ? 'Registro Médico / Editar Paciente' : 
                                 'Registro Médico / Detalle de Paciente'}
                            </h5>
                            <button type="button" onClick={() => confirmCloseModal(() => setShowModal(false))} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, padding: 0, cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, backgroundColor: '#f8f9fa' }}>
                            <FormularioPaciente 
                                onSave={handleSave} 
                                onCancel={() => confirmCloseModal(() => setShowModal(false))} 
                                initialData={selectedPatient}
                                clinicalData={clinicalData}
                                isViewOnly={modalMode === 'view'}
                                quoteIdToComplete={quoteIdToComplete}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showHistoriaModal && (
                <VisorHistoriaClinica 
                    patient={selectedPatient} 
                    onCancel={() => confirmCloseModal(() => setShowHistoriaModal(false))} 
                />
            )}

            {showCertificadoModal && (
                <CertificadoMedico 
                    patient={selectedPatient} 
                    clinicalData={clinicalData}
                    onSaveSuccess={() => {
                        loadPacientes();
                        if (selectedPatient?.id) loadPatientClinicalData(selectedPatient.id);
                    }}
                    onCancel={(direct = false) => {
                        if (direct === true) {
                            setShowCertificadoModal(false);
                        } else {
                            confirmCloseModal(() => setShowCertificadoModal(false));
                        }
                    }} 
                />
            )}

            {showConsultaModal && (
                <VisorConsultaModal 
                    patient={selectedPatient} 
                    quote={selectedQuote} 
                    onCancel={() => confirmCloseModal(() => setShowConsultaModal(false))} 
                />
            )}

            {showRegistroFotograficoModal && (
                <RegistroFotografico 
                    patient={selectedPatient} 
                    onCancel={() => confirmCloseModal(() => setShowRegistroFotograficoModal(false))} 
                />
            )}
            {showObsModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '90%', maxWidth: '600px', maxHeight: '80vh', backgroundColor: '#fff', borderRadius: '10px', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#6c757d', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                                Observaciones - {selectedObsPatient?.first_name} {selectedObsPatient?.last_name}
                            </h5>
                            <button type="button" onClick={() => confirmCloseModal(() => setShowObsModal(false))} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, padding: 0, cursor: 'pointer' }}>&times;</button>
                        </div>
                        
                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, backgroundColor: '#f8f9fa' }}>
                            {observations.length === 0 ? (
                                <p className="text-muted text-center my-4">No hay observaciones registradas para este paciente.</p>
                            ) : (
                                observations.map((obs, index) => (
                                    <div key={obs.id || index} style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '15px', marginBottom: '15px', position: 'relative' }}>
                                        <button 
                                            onClick={() => handleRemoveObservation(index)}
                                            style={{ position: 'absolute', top: '10px', right: '10px', background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                            title="Eliminar esta observación"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                        
                                        <div className="mb-2 pe-4">
                                            <textarea 
                                                className="form-control" 
                                                rows="2" 
                                                value={obs.text} 
                                                onChange={(e) => handleUpdateObservation(index, 'text', e.target.value)}
                                                placeholder="Escribe la observación aquí..."
                                                style={{ resize: 'vertical' }}
                                            ></textarea>
                                        </div>
                                        
                                        <div className="form-check form-switch mt-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id={`obsResolvedSwitch_${index}`} 
                                                checked={obs.resolved} 
                                                onChange={(e) => handleUpdateObservation(index, 'resolved', e.target.checked)}
                                                style={{ cursor: 'pointer', height: '20px', width: '40px' }}
                                            />
                                            <label className="form-check-label fw-bold ms-2" htmlFor={`obsResolvedSwitch_${index}`} style={{ cursor: 'pointer', color: obs.resolved ? '#28a745' : '#dc3545', paddingTop: '2px' }}>
                                                {obs.resolved ? (
                                                    <><i className="bi bi-check-circle-fill me-1"></i> Resuelta</>
                                                ) : (
                                                    <><i className="bi bi-plug-fill me-1"></i> Pendiente (No resuelta)</>
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                ))
                            )}
                            
                            <div className="text-center mt-3">
                                <button type="button" className="btn btn-light border" onClick={handleAddObservation}>
                                    <i className="bi bi-plus-circle me-1"></i> Agregar Observación
                                </button>
                            </div>
                        </div>

                        <div style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#fff' }}>
                            <button type="button" className="btn btn-light border me-2" onClick={() => confirmCloseModal(() => setShowObsModal(false))}>Cancelar</button>
                            <button type="button" className="btn btn-dark" onClick={handleSaveObservations} disabled={savingObs}>
                                {savingObs ? 'Guardando...' : 'Guardar Observaciones'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            

            {showMissingFieldsModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '90%', maxWidth: '400px', backgroundColor: '#fff', borderRadius: '10px', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#dc3545', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                                Datos Incompletos
                            </h5>
                            <button type="button" onClick={() => setShowMissingFieldsModal(false)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, padding: 0, cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
                            <p style={{ fontSize: '1rem', marginBottom: '15px', color: '#333' }}>
                                No tiene los campos completos como para emitir un certificado, por favor antes de emitir un certificado complete los campos:
                            </p>
                            <ul style={{ listStyleType: 'none', padding: 0, margin: 0, color: '#dc3545', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                {missingFields.map((field, idx) => (
                                    <li key={idx} style={{ marginBottom: '5px' }}>
                                        <i className="bi bi-exclamation-circle me-2"></i>{field}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
                            <button type="button" className="btn btn-dark" onClick={() => setShowMissingFieldsModal(false)}>
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <ConfirmModal 
                isOpen={!!confirmAction} 
                onClose={() => setConfirmAction(null)} 
                onConfirm={() => {
                    if (confirmAction) confirmAction();
                    setConfirmAction(null);
                }} 
            />
        </div>
    );
};

export default Pacientes;
